import { useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { BookOpen, History, PlugZap, RefreshCw, Settings2, Unplug } from 'lucide-react';
import { Accordion, Button, Card, CardGridSkeleton, ConfirmDialog, Fact, Facts, PageHeader, ProgressBar, StatusPill, Tabs } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { IntegrationDialog } from '@/components/integrations/IntegrationDialog';
import { IntegrationMark } from '@/components/integrations/IntegrationMark';
import { IntegrationUsageCard } from '@/components/integrations/IntegrationUsage';
import { useCheckIntegration, useIntegration, useIntegrations, useRemoveIntegration } from '@/hooks/useIntegrations';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatDateTime, formatRelative } from '@/utils/format';
import {
  INTEGRATION_DOT,
  INTEGRATION_EVENT_LABELS,
  INTEGRATION_NAMES,
  INTEGRATION_ORDER,
  INTEGRATION_PURPOSE,
  INTEGRATION_STATE,
  INTEGRATION_STEPS,
  integrationState,
  isIntegrationKind,
} from '@/utils/integrations';
import type { IntegrationDetailView, IntegrationKind } from '@/types/api';

const count = (value: number) => value.toLocaleString('ru-RU');

/** What may be shown of a set integration — never the key — with its quota where it has one. */
function Settings({ detail }: { readonly detail: IntegrationDetailView }): ReactNode {
  const { integration } = detail;
  const settings = (integration.settings ?? {}) as Record<string, unknown>;
  const data = integration.checkData;
  switch (integration.kind) {
    case 'google':
      return (
        <>
          <Fact label="Сервисный аккаунт">
            <span className="mono">{String(settings['serviceAccount'] ?? '—')}</span>
          </Fact>
          <Fact label="Владельцы">{Array.isArray(settings['ownerEmails']) && settings['ownerEmails'].length > 0 ? (settings['ownerEmails'] as string[]).join(', ') : <span className="faint">не указаны</span>}</Fact>
          {data?.properties !== undefined ? <Fact label="Ресурсов у аккаунта">{data.properties}</Fact> : null}
        </>
      );
    case 'anthropic':
      return (
        <>
          <Fact label="Ключ">{String(settings['keyHint'] ?? '••••')}</Fact>
          <Fact label="Модель">{settings['model'] === 'claude-sonnet-5' ? 'Claude Sonnet 5' : 'Claude Opus 5'}</Fact>
        </>
      );
    case 'ahrefs':
      return (
        <>
          <Fact label="Ключ">{String(settings['keyHint'] ?? '••••')}</Fact>
          <Fact label="Тариф">{data?.freeKey ? 'бесплатный ключ: только текущий DR' : (data?.plan ?? <span className="faint">{integration.checkError ? '—' : 'узнаем при проверке'}</span>)}</Fact>
          <Fact label="История">{settings['history'] === false ? 'выключена' : data?.freeKey ? 'недоступна на бесплатном ключе' : 'по месяцам, за 5 лет'}</Fact>
          {data?.keyExpiresAt ? <Fact label="Ключ действует до">{formatDateTime(data.keyExpiresAt)}</Fact> : null}
        </>
      );
    case 'dataforseo':
      return (
        <>
          <Fact label="Логин">{String(settings['login'] ?? '—')}</Fact>
          {data?.balanceUsd !== undefined && data.balanceUsd !== null ? <Fact label="Баланс">${data.balanceUsd.toFixed(2)}</Fact> : null}
        </>
      );
    case 'openpagerank':
      return <Fact label="Ключ">{String(settings['keyHint'] ?? '••••')}</Fact>;
  }
}

/** Ahrefs's units this month against the key's limit: the one quota the page can draw. */
function Quota({ detail }: { readonly detail: IntegrationDetailView }): ReactNode {
  const data = detail.integration.checkData;
  if (detail.integration.kind !== 'ahrefs' || !data || data.freeKey || data.unitsUsed === null || data.unitsUsed === undefined || !data.unitsLimit) return null;
  const share = data.unitsUsed / data.unitsLimit;
  return (
    <div className="stack stack-tight">
      <div className="row-between text-sm">
        <span>Единиц API в этом месяце</span>
        <span className="numeric">
          {count(data.unitsUsed)} из {count(data.unitsLimit)}
        </span>
      </div>
      <ProgressBar value={data.unitsUsed} max={data.unitsLimit} label="Единицы API Ahrefs" tone={share >= 0.9 ? 'down' : share >= 0.7 ? 'degraded' : 'accent'} />
      {data.unitsResetAt ? <span className="muted text-sm">Сброс {formatDateTime(data.unitsResetAt)}</span> : null}
    </div>
  );
}

/** One integration's tab (ADR-0052): state and settings, what it does, its history, and how to set it up. */
function IntegrationDetail({ kind, onEdit }: { readonly kind: IntegrationKind; readonly onEdit: () => void }): ReactNode {
  const detail = useIntegration(kind);
  const [confirm, setConfirm] = useState(false);
  const check = useCheckIntegration(kind, {
    onSuccess: (result) => {
      const checked = result.integrations.find((item) => item.kind === kind);
      if (checked?.checkError) toast.error('Проверка не прошла', checked.checkError);
      else toast.success('Ключ принят');
    },
    onError: (error) => toast.error('Не удалось проверить', errorText(error)),
  });
  const remove = useRemoveIntegration(kind, { onSuccess: () => setConfirm(false) });

  if (detail.isPending) return <CardGridSkeleton cards={2} />;
  if (detail.isError) return <ErrorState error={detail.error} />;
  const view = detail.data;
  const { integration } = view;
  const state = integrationState(integration);

  return (
    <div className="stack">
      {/* The Card's own markup: its title takes a lucide icon, and this one carries the service's mark. */}
      <section className="card">
        <header className="card-head">
          <h3 className="card-title">
            <IntegrationMark kind={kind} size={18} />
            {INTEGRATION_NAMES[kind]}
          </h3>
          <div className="row-tight">
            <StatusPill map={INTEGRATION_STATE} status={check.isPending ? 'unchecked' : state} />
            <Button size="sm" variant={integration.configured ? 'default' : 'primary'} icon={integration.configured ? Settings2 : PlugZap} onClick={onEdit}>
              {integration.configured ? 'Изменить' : 'Подключить'}
            </Button>
            {integration.configured ? (
              <>
                <Button size="sm" icon={RefreshCw} busy={check.isPending} onClick={() => check.mutate()}>
                  Проверить
                </Button>
                <Button size="sm" variant="ghost" icon={Unplug} onClick={() => setConfirm(true)}>
                  Отключить
                </Button>
              </>
            ) : null}
          </div>
        </header>
        <div className="card-body stack">
          <p className="muted">{INTEGRATION_PURPOSE[kind]}</p>
          {integration.configured ? (
            <>
              <Facts>
                <Settings detail={view} />
                <Fact label="Последняя проверка">
                  {check.isPending ? (
                    <span className="muted">проверяем…</span>
                  ) : integration.checkError ? (
                    <span className="tone-down">{integration.checkError}</span>
                  ) : integration.checkNote ? (
                    <span>{integration.checkNote}</span>
                  ) : (
                    <span className="faint">не выполнялась</span>
                  )}
                  {integration.checkedAt && !check.isPending ? <span className="muted"> · {formatRelative(integration.checkedAt)}</span> : null}
                </Fact>
                {view.nextCheckAt ? <Fact label="Следующая проверка">{formatRelative(view.nextCheckAt)} · по расписанию каждые 6 ч</Fact> : null}
                {integration.updatedAt ? (
                  <Fact label="Изменено">
                    {integration.updatedBy} · {formatRelative(integration.updatedAt)}
                  </Fact>
                ) : null}
              </Facts>
              <Quota detail={view} />
            </>
          ) : null}
        </div>
      </section>

      {integration.configured ? <IntegrationUsageCard usage={view.usage} configured={integration.configured} /> : null}

      {view.events.length > 0 ? (
        <Card flush title="История" icon={History}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Что</th>
                  <th scope="col">Итог</th>
                  <th scope="col">Кто</th>
                </tr>
              </thead>
              <tbody>
                {view.events.map((event, index) => (
                  <tr key={`${event.at}-${index}`}>
                    <td className="nowrap" title={formatDateTime(event.at)}>
                      {formatRelative(event.at)}
                    </td>
                    <td>{INTEGRATION_EVENT_LABELS[event.type]}</td>
                    <td className="text-sm">
                      {event.error ? <span className="tone-down">{event.error}</span> : event.note ? event.note : event.ok ? 'готово' : '—'}
                    </td>
                    <td>{event.by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      <Accordion title="Как подключить" icon={BookOpen} open={!integration.configured}>
        <ol className="stack stack-tight">
          {INTEGRATION_STEPS[kind].map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
      </Accordion>

      <ConfirmDialog
        open={confirm}
        title={`Отключить ${INTEGRATION_NAMES[kind]}?`}
        description="Ключ будет удалён из панели. История останется."
        confirmLabel="Отключить"
        destructive
        busy={remove.isPending}
        error={remove.isError ? errorText(remove.error) : undefined}
        onConfirm={() => remove.mutate()}
        onClose={() => setConfirm(false)}
      />
    </div>
  );
}

/**
 * Keys of outside services (ADR-0048), for owners and administrators: a tab
 * per service, each its own page with its state, what it does for the panel
 * and what happened to it (ADR-0052).
 */
export function IntegrationsPage(): ReactNode {
  const { kind } = useParams();
  const navigate = useNavigate();
  const integrations = useIntegrations();
  const [editing, setEditing] = useState(false);

  if (!isIntegrationKind(kind)) return <Navigate to={`/integrations/${INTEGRATION_ORDER[0]}`} replace />;
  const views = integrations.data?.integrations ?? [];

  return (
    <div className="page">
      <PageHeader title="Интеграции" subtitle="Ключи внешних сервисов и что они делают для панели" />
      <Tabs
        label="Сервисы"
        active={kind}
        onChange={(next) => navigate(`/integrations/${next}`)}
        tabs={INTEGRATION_ORDER.map((item) => ({
          id: item,
          label: INTEGRATION_NAMES[item],
          mark: <IntegrationMark kind={item} size={15} />,
          ...(integrations.data ? { dot: INTEGRATION_DOT[integrationState(views.find((view) => view.kind === item))] } : {}),
        }))}
      />
      <div role="tabpanel" id={`panel-${kind}`} aria-labelledby={`tab-${kind}`} className="tab-panel">
        <IntegrationDetail key={kind} kind={kind} onEdit={() => setEditing(true)} />
      </div>
      {editing ? <IntegrationDialog kind={kind} view={views.find((view) => view.kind === kind)} onClose={() => setEditing(false)} /> : null}
    </div>
  );
}
